import React, { useState, useEffect } from 'react';
import { Download } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { licenseRequestService } from '../services/licenseRequestService';
import { API_URL } from '../config';
import apiClient from '../services/apiClient';
import toast from 'react-hot-toast';

const MyRequestsPage = () => {
  const [licenseRequests, setLicenseRequests] = useState([]);
  const [clusterRequests, setClusterRequests] = useState([]);
  const [activationKeys, setActivationKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'cluster', 'activation'
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Set page title
  useEffect(() => {
    document.title = 'My Requests | Customer Portal';
  }, []);

  useEffect(() => {
    loadAllRequests();
  }, []);

  const loadAllRequests = async () => {
    try {
      // Load cluster license requests
      const clusterData = await apiClient.get('/cluster-licenses/my-licenses');
      setClusterRequests(Array.isArray(clusterData) ? clusterData : []);

      // Load activation keys using apiClient
      const activationData = await apiClient.get('/activation-keys/my-keys');
      setActivationKeys(Array.isArray(activationData) ? activationData : []);
    } catch (error) {
      console.error('Error loading requests:', error);
      setClusterRequests([]);
      setActivationKeys([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      APPROVED: 'bg-green-100 text-green-800',
      REJECTED: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const downloadSignedFile = async (id, hostname) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/activation-keys/download/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `activation-${hostname}-signed.xlic`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        toast.error('Failed to download signed file');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download signed file');
    }
  };

  // Filter combined requests
  const getFilteredRequests = () => {
    let combined = [];
    
    if (filter === 'all' || filter === 'cluster') {
      combined = [...combined, ...clusterRequests.map(r => ({...r, type: 'cluster'}))];
    }
    
    if (filter === 'all' || filter === 'activation') {
      combined = [...combined, ...activationKeys.map(k => ({...k, type: 'activation'}))];
    }
    
    // Sort by date
    combined.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return combined;
  };

  const filteredRequests = getFilteredRequests();
  const totalCount = clusterRequests.length + activationKeys.length;
  
  // Pagination calculations
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRequests = filteredRequests.slice(startIndex, endIndex);
  
  // Reset to page 1 when filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Loading your requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">My Requests</h2>
            <span className="text-sm text-gray-500">
              {totalCount} request{totalCount !== 1 ? 's' : ''} total
            </span>
          </div>
          
          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilter('all')}
              style={filter === 'all' ? { backgroundColor: '#197560' } : {}}
              className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                filter === 'all'
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({totalCount})
            </button>
            <button
              onClick={() => setFilter('cluster')}
              style={filter === 'cluster' ? { backgroundColor: '#197560' } : {}}
              className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                filter === 'cluster'
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Cluster License Requests ({clusterRequests.length})
            </button>
            <button
              onClick={() => setFilter('activation')}
              style={filter === 'activation' ? { backgroundColor: '#197560' } : {}}
              className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors ${
                filter === 'activation'
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Activation Keys ({activationKeys.length})
            </button>
          </div>
        </div>

        {filteredRequests.length === 0 ? (
          <div className="p-8 text-center">
            <div className="mx-auto w-12 h-12 text-gray-400 mb-4">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No requests found</h3>
            <p className="text-gray-600 mb-4">
              {filter === 'all' && "You haven't submitted any requests yet."}
              {filter === 'cluster' && "You haven't submitted any cluster license requests yet."}
              {filter === 'activation' && "You haven't uploaded any activation keys yet."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Request Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    System Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedRequests.map((request) => (
                  <tr key={`${request.type}-${request.id}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        request.type === 'cluster' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-purple-100 text-purple-800'
                      }`}>
                        {request.type === 'cluster' ? 'Cluster' : 'Activation'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {request.hostname || 'Unknown'}
                      </div>
                      {request.originalFilename && (
                        <div className="text-xs text-gray-500 truncate max-w-xs">
                          {request.originalFilename}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {request.type === 'cluster' ? (
                        <div className="text-sm text-gray-700">
                          {request.totalHosts || 0} hosts, {request.totalSockets || 0} sockets
                        </div>
                      ) : (
                        <>
                          {request.cpuSockets && request.cpuCores ? (
                            <div className="text-sm text-gray-700">
                              {request.cpuSockets} sockets, {request.cpuCores} cores
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">N/A</span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(request.status)}
                      {request.responseNotes && (
                        <div className="text-xs text-gray-500 mt-1">
                          {request.responseNotes}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>{formatDate(request.createdAt)}</div>
                      {request.approvedAt && (
                        <div className="text-xs">
                          Processed: {formatDate(request.approvedAt)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {request.type === 'activation' && request.status === 'APPROVED' && request.signedFilePath && (
                        <button
                          onClick={() => downloadSignedFile(request.id, request.hostname)}
                          className="inline-flex items-center text-green-600 hover:text-green-700 font-medium"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </button>
                      )}
                      {request.status === 'PENDING' && (
                        <span className="text-gray-400 text-xs">Pending</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination Controls */}
        {filteredRequests.length > itemsPerPage && (
          <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              Showing {Math.min(startIndex + 1, filteredRequests.length)} to {Math.min(endIndex, filteredRequests.length)} of {filteredRequests.length} requests
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              {(() => {
                const pages = [];
                for (let i = 1; i <= totalPages; i++) {
                  if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1 border rounded text-sm transition-colors ${
                          currentPage === i
                            ? 'bg-green-600 text-white border-green-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  } else if (i === currentPage - 2 || i === currentPage + 2) {
                    pages.push(<span key={i} className="px-2 text-gray-500">...</span>);
                  }
                }
                return pages;
              })()}
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyRequestsPage;